import MedicaidLeadContext from "contexts/MedicaidLeadContext";
import React, { useContext, useEffect } from "react";
import { Redirect, useParams } from "react-router-dom";
import {
  fetchMedicaidLead,
  initialiseMedicaidLead,
} from "modules/medicaid/actions";
import { shallowEqual, useDispatch } from "react-redux";
import MedicaidLeadInterview from "../MedicaidLeadInterview";
import { useSelector } from "redux/reducer";
import { getPartnerRoute, ROUTE_TEMPLATE } from "modules/navigation/routes";
import {
  formatPartner,
  formatPartnerForRoute,
} from "modules/navigation/helpers/getFormattedPartnerId";
import { go } from "modules/navigation/helpers/navigator";
import AnimatedLoadingOverlay from "components/AnimatedLoadingOverlay";
import { AnimatedLoadingOverlaySize } from "components/AnimatedLoadingOverlay/types";

const MedicaidLeadContainer = () => {
  const dispatch = useDispatch();
  const { lead, isLoadingMedicaidLead } = useContext(MedicaidLeadContext);

  const { jurisdiction, partnerFromStore } = useSelector(
    (state) => ({
      jurisdiction: state.state,
      partnerFromStore: state.partner?.id,
    }),
    shallowEqual,
  );

  const { partnerId: urlPartnerId, medicaidLeadId } = useParams<{
    partnerId?: string;
    medicaidLeadId?: string;
  }>();

  const effectivePartnerId = urlPartnerId || partnerFromStore;

  // Legacy URL (no partnerId in path): after fetch, redirect to partner-scoped URL
  useEffect(() => {
    if (
      !urlPartnerId &&
      medicaidLeadId &&
      lead?.partnerId &&
      !isLoadingMedicaidLead
    ) {
      go(
        dispatch,
        getPartnerRoute(
          formatPartnerForRoute(lead.partnerId),
          ROUTE_TEMPLATE.PARTNER_MEDICAID_LEAD,
          { medicaidLeadId },
        ),
      );
    }
  }, [
    dispatch,
    lead?.partnerId,
    medicaidLeadId,
    isLoadingMedicaidLead,
    urlPartnerId,
  ]);

  if (jurisdiction === null) {
    return <Redirect to={ROUTE_TEMPLATE.ROOT} />;
  }

  // New lead: require firm (partner) in context
  if (medicaidLeadId === undefined) {
    if (!effectivePartnerId) {
      return <Redirect to={ROUTE_TEMPLATE.ROOT} />;
    }
    if (!isLoadingMedicaidLead) {
      initialiseMedicaidLead(
        dispatch,
        jurisdiction,
        formatPartner(effectivePartnerId),
      );
    }
  } else if (lead === undefined && !isLoadingMedicaidLead) {
    fetchMedicaidLead(dispatch, medicaidLeadId);
  }

  // Legacy URL: show loading until redirect
  if (!urlPartnerId && medicaidLeadId && lead?.partnerId) {
    return <AnimatedLoadingOverlay size={AnimatedLoadingOverlaySize.LARGE} />;
  }

  if (lead === undefined || isLoadingMedicaidLead) {
    return <AnimatedLoadingOverlay size={AnimatedLoadingOverlaySize.LARGE} />;
  }

  return <MedicaidLeadInterview />;
};

export default MedicaidLeadContainer;
